import { minimumDelay } from "@circle-react/helpers/dateTimeHelpers";
import type { PostSpaceSummary } from "@circle-react/types/AiSummary/SpaceSummary";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { SpaceDateRange } from "@/react/types/AiSummary/SpaceSummary";
import { SPACE_DATE_RANGE } from "@/react/types/AiSummary/SpaceSummary";
import type { Space } from "@/react/types/Space";

interface UseSpaceSummaryProps {
  dateFrom?: string;
  dateRange: SpaceDateRange;
  enabled: boolean;
  firstUnreadMessageId?: string;
  space: Space;
  type: "post" | "chat";
}

interface ErrorResponse {
  body?: {
    message?: string;
    error_details?: {
      noDataToSummarize?: boolean;
      isContentCutOff?: boolean;
    };
  };
}

export const useSpaceSummary = ({
  space,
  dateFrom,
  enabled,
  type,
  firstUnreadMessageId,
  dateRange,
}: UseSpaceSummaryProps) => {
  const [hasDelayedNoDataError, setHasDelayedNoDataError] = useState(false);
  const REQUEST_KEY =
    type === "post" ? "post-space-summary" : "chat-space-summary";
  const queryKey = [
    REQUEST_KEY,
    space.id,
    ...(type === "post" ? [space.posts_count] : []),
    dateRange,
    ...(dateRange === SPACE_DATE_RANGE.FROM_UNREAD && firstUnreadMessageId
      ? [firstUnreadMessageId]
      : []),
  ];
  const queryClient = useQueryClient();

  const { data, isInitialLoading, error, refetch } = useQuery<
    PostSpaceSummary,
    ErrorResponse
  >({
    queryKey: queryKey,
    queryFn: () => {
      const params: Record<string, any> = {};
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (firstUnreadMessageId) {
        params.first_unread_message_id = firstUnreadMessageId;
      }

      return reactQueryGet(
        internalApi.spaces.aiSummaries.index({
          spaceId: String(space.id),
          params,
        }),
      );
    },
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (_, error: any) =>
      error?.status !== 404 && error?.status !== 401 && error?.status !== 400,
  });

  const clearCache = () => {
    void queryClient.resetQueries({ queryKey, exact: true });
  };

  useEffect(() => {
    const hasNoDataError = error?.body?.error_details?.noDataToSummarize;

    if (hasNoDataError) {
      void minimumDelay(Promise.resolve(), 3000).then(() => {
        setHasDelayedNoDataError(true);
      });
    } else {
      setHasDelayedNoDataError(false);
    }
  }, [error]);

  const contentCutOffMessage = error?.body?.error_details?.isContentCutOff
    ? (error.body.message ?? null)
    : null;

  return {
    spaceSummary: data,
    isLoadingSpaceSummary: isInitialLoading,
    refetchSpaceSummary: refetch,
    clearSpaceSummary: clearCache,
    isNoDataError: hasDelayedNoDataError,
    contentCutOffMessage,
  };
};
